import { hasValue } from '@/modules/core/data/record';
import {
  getStepFullLabel,
  shouldDisplayStepTuitionFees,
} from '@/modules/solutions/educationalProgramStep';
import {
  findNextItem,
  findPreviousItem,
} from '@/modules/core/js/array';
import { Amplitude } from '@amplitude/react-amplitude';
import {
  css,
  useTheme,
} from '@emotion/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classnames from 'classnames';
import filter from 'lodash.filter';
import get from 'lodash.get';
import isEqual from 'lodash.isequal';
import map from 'lodash.map';
import some from 'lodash.some';
import React, {
  Fragment,
  useState,
} from 'react';
import {
  Trans,
  useTranslation,
} from 'react-i18next';

import { LogEvent } from '@/modules/core/amplitude/types/Amplitude';
import { Asset } from '@/modules/core/data/types/Asset';
import { CustomerTheme } from '@/modules/core/data/types/CustomerTheme';
import { EducationalProgramStep } from '@/modules/core/data/types/EducationalProgramStep';
import { GenericObject } from '@/modules/core/data/types/GenericObject';
import { AMPLITUDE_ACTIONS } from '@/modules/core/amplitude/amplitude';
import Btn from '@/components/dataDisplay/Btn';
import DocumentButton from '@/components/dataDisplay/DocumentButton';
import ExternalLink from '@/components/dataDisplay/ExternalLink';
import Markdown from '@/components/dataDisplay/Markdown';
import EducationalProgramStepsFeesNavBar from './EducationalProgramStepsFeesNavBar';

type Props = {
  selectedEducationalProgramSteps: Array<EducationalProgramStep>;
}

const EducationalProgramStepFees: React.FunctionComponent<Props> = (props): JSX.Element => {
  const {
    selectedEducationalProgramSteps,
  }: Props = props;
  const selectedEducationalProgramStepsWithTuitionFees = filter(selectedEducationalProgramSteps, shouldDisplayStepTuitionFees);
  const { t } = useTranslation();
  const theme: CustomerTheme = useTheme();
  const { surfaceColor, primaryColor } = theme;
  const [isDisplayingFees, setIsDisplayingFees] = useState<boolean>(false);
  const [transition, setTransition] = useState<string>(null);
  const [educationalProgramSteps, setEducationalProgramSteps] = useState<EducationalProgramStep[]>(selectedEducationalProgramStepsWithTuitionFees);
  const [activeEducationalProgramStep, setActiveEducationalProgramStep] = useState<EducationalProgramStep>(selectedEducationalProgramStepsWithTuitionFees?.[0]);

  // Sync state (from older props) with newer props
  // XXX This ensures that we keep displaying up-to-date information when the user selects different program steps in the ContextBuilder
  if (!isEqual(educationalProgramSteps, selectedEducationalProgramStepsWithTuitionFees)) {
    setEducationalProgramSteps(selectedEducationalProgramStepsWithTuitionFees);
    setActiveEducationalProgramStep(selectedEducationalProgramStepsWithTuitionFees?.[0]);
  }

  const hasAnyTuitionFeesDescription: boolean = some(educationalProgramSteps, (educationalProgramStep: EducationalProgramStep) => {
    return hasValue(educationalProgramStep, 'tuitionFeesDescription');
  });

  if (!activeEducationalProgramStep || !hasAnyTuitionFeesDescription) {
    return null;
  }

  const activeStepFeesDescription: string = get(activeEducationalProgramStep, 'tuitionFeesDescription', t('solutionsPage.programStepFees.feesDescriptionMissing.text', 'Aucune information'));
  const activeStepDocuments: Asset[] = get(activeEducationalProgramStep, 'tuitionFeesDocuments', []);
  const previousStep: EducationalProgramStep = findPreviousItem(activeEducationalProgramStep, selectedEducationalProgramStepsWithTuitionFees);
  const nextStep: EducationalProgramStep = findNextItem(activeEducationalProgramStep, selectedEducationalProgramStepsWithTuitionFees);

  return (
    <Amplitude
      eventProperties={(inheritedProps): GenericObject => ({
        ...inheritedProps,
      })}
    >
      {({ logEvent }: { logEvent: LogEvent }): JSX.Element => (
        <div
          css={css`
            margin: 10px 0px 10px 0px;

            .tuition-fee-cta {
              justify-content: center;
              text-align: center;
            }

            b {
              color: ${primaryColor};
            }

            .tuition-fees-container {
              background-color: ${surfaceColor};
              border-radius: 25px;
              margin-left: 20px;
              margin-right: 10px;
              display: inline-flex;
              flex-wrap: wrap;
              justify-content: space-between;
              flex-direction: row;
              align-items: center;
              width: 97%;

              @media (max-width: 991.98px) {
                max-width: 90%;
              }
            }

            .tuition-fees-title {
              display: flex;
              margin-top: 20px;
            }

            .tuition-fees-description {
              max-width: 70%;
              padding-left: 15px;
              padding-right: 10px;

              @media (max-width: 991.98px) {
                max-width: 90%;
              }
            }

            .tuition-fees-assets {
              display: flex;
              flex-direction: column;

              @media (max-width: 991.98px) {
                width: 100%;
                justify-content: center;
                align-items: center;
                text-align: center;
                margin-bottom: 20px;
              }
            }

            .animated {
              animation: ease-in-out;
              animation-duration: 400ms;
              animation-fill-mode: both;
            }

            .fadeInUp {
              opacity: 0;
              animation-name: fadeInUp;

              @keyframes fadeInUp {
                from {
                  transform: translate3d(0, -40px, 0)
                }
                to {
                  transform: translate3d(0, 0, 0);
                  opacity: 1
                }
              }
            }

            .fadeInDown {
              opacity: 0;
              animation-name: fadeInDown;

              @keyframes fadeInDown {
                from {
                  transform: translate3d(0, 40px, 0)
                }
                to {
                  transform: translate3d(0, 0, 0);
                  opacity: 1
                }
              }
            }
          `}
        >
          {
            !isDisplayingFees ? (
              <Fragment>
                {
                  // If we aren't displaying the fees (user hasn't clicked yet), then display the spoiler button,
                  // but only if there are some fees to be shown in any step (otherwise it'd be pointless to display a CTA that won't show anything useful)
                  hasAnyTuitionFeesDescription && (
                    <div className={'tuition-fee-cta'}>
                      <Btn
                        onClick={(): void => {
                          setIsDisplayingFees(true);

                          logEvent('solutions-tuitionFees-displayed', {
                            selection: {
                              ref: activeEducationalProgramStep.ref,
                              isDefault: true,
                            },
                            action: AMPLITUDE_ACTIONS.CLICK,
                          });
                        }}
                      >
                        <FontAwesomeIcon icon="info-circle" />
                        {t('solutionsPage.programStepFees.displayFees.button', 'Voir le prix de la scolarité')}
                      </Btn>
                    </div>
                  )
                }
              </Fragment>
            ) : (
              <Fragment>
                <div className={'tuition-fees-container'}>
                  <EducationalProgramStepsFeesNavBar
                    selectedEducationalProgramSteps={selectedEducationalProgramStepsWithTuitionFees}
                    setActiveEducationalProgramStep={setActiveEducationalProgramStep}
                    setTransition={setTransition}
                    previousStep={previousStep}
                    nextStep={nextStep}
                  />
                  <div
                    className={classnames('tuition-fees-description', {
                      'fadeInUp animated': transition === 'up',
                      'fadeInDown animated': transition === 'down',
                    })}
                  >
                    <div className={'tuition-fees-title'}>
                      <FontAwesomeIcon
                        icon="info-circle"
                      />
                      <h3>
                        <Trans
                          i18nKey={'solutionsPage.programStepFees.title'}
                        >
                          Frais de scolarité pour <b>{{ label: getStepFullLabel(activeEducationalProgramStep, { preferShortProgramLabel: false }) }}</b>
                        </Trans>
                      </h3>
                    </div>
                    <Markdown text={activeStepFeesDescription} />
                  </div>
                  {
                    activeStepDocuments ? (
                      <div className={'tuition-fees-assets'}>
                        {
                          map(activeStepDocuments, (asset: Asset) => {
                            return (
                              <ExternalLink
                                href={asset?.url}
                              >
                                <DocumentButton>
                                  {asset?.title}
                                </DocumentButton>
                              </ExternalLink>
                            );
                          })
                        }
                      </div>
                    ) : null
                  }
                </div>
              </Fragment>
            )
          }
        </div>
      )}
    </Amplitude>
  );
};

export default EducationalProgramStepFees;
